/**
 * SetPassword — landing for password-less stub accounts (legacy waitlist
 * signups migrated into the unified users table). Pre-fills the email from
 * `?email=` URL param (sent in the wave-publish email link), asks for a
 * password + confirm, calls /auth/set-password, then routes to the patron
 * profile (which forces DNA completion via the incomplete-profile redirect).
 *
 * No DOB / name / phone here — those live on the profile DNA step. This
 * screen does ONE thing: turn a confirmed-email-no-password stub into a
 * logged-in user.
 */
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import PasswordInput, { PASSWORD_MIN_LENGTH, validatePassword } from "../components/PasswordInput";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function SetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  // Email comes pre-filled from the broadcast email link (?email=…) and is
  // LOCKED — the user identifies themselves by clicking their personalised
  // link, not by re-typing.
  const emailFromLink = (params.get("email") || "").toLowerCase().trim();
  const tokenFromLink = params.get("token") || "";
  // PHASE 8.35 — the wave-publish email carries a `next` query param so the
  // patron lands on the right post-password page (typically the profile
  // completion screen with an `after=` deep-link to the actual survey).
  const nextFromLink = params.get("next") || "/panelist/profile";
  const [email, setEmail] = useState(emailFromLink);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState(null);
  useEffect(() => {
    if (!email) return;
    api.get(`/auth/account-state?email=${encodeURIComponent(email)}`).then((r) => setProbe(r.data)).catch(() => {});
  }, [email]);
  const emailLocked = !!emailFromLink;
  const accountKnown = probe && probe.exists;
  const alreadyHasPassword = probe && probe.exists && !probe.needs_password_set;

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    const pwErr = validatePassword(pw);
    if (pwErr) { toast.error(pwErr); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { email, password: pw, token: tokenFromLink || undefined });
      if (data.token) localStorage.setItem("pollpak_token", data.token);
      setUser(data.user);
      toast.success("Password set. Welcome to oodel.");
      nav(nextFromLink, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't set password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      <div className="max-w-md w-full bg-white border-2 border-black p-8" data-testid="set-password-page">
        <Logo size="sm" />
        <div className="mt-6 mb-2 overline text-mango">FINISH SETTING UP</div>
        <h1 className="text-3xl font-black tracking-tight">Set your password</h1>
        <p className="text-sm text-neutral-600 mt-2 mb-6">
          You joined our waitlist earlier — set a password now to access your account and start earning points.
        </p>

        {alreadyHasPassword && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-xs text-amber-900 mb-4">
            This account already has a password. <Link to="/login" className="underline font-bold">Sign in instead →</Link>
          </div>
        )}

        {/* Locked identity card — pulled from the link + verified via probe. */}
        {accountKnown && probe.name && (
          <div className="border-2 border-black bg-neutral-50 p-3 mb-4 text-sm" data-testid="setpw-locked-identity">
            <div className="overline text-xs text-neutral-500 mb-0.5">Account</div>
            <div className="font-bold">{probe.name}</div>
            <div className="text-neutral-600 text-xs mt-0.5">{email}</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {!emailLocked && (
            <div>
              <Label className="overline text-xs">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value.toLowerCase().trim())} className="rounded-none" data-testid="setpw-email" />
            </div>
          )}
          <div>
            <Label className="overline text-xs">New password (min {PASSWORD_MIN_LENGTH} chars)</Label>
            <PasswordInput testId="setpw-pw" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus autoComplete="new-password" />
          </div>
          <div>
            <Label className="overline text-xs">Confirm password</Label>
            <PasswordInput testId="setpw-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
          </div>
          <Button type="submit" disabled={busy || alreadyHasPassword} className="w-full bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none" data-testid="setpw-submit">
            {busy ? "Setting…" : "Set password & continue →"}
          </Button>
        </form>

        <p className="mt-6 text-xs text-neutral-500">
          Already have a password? <Link to="/login" className="underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
